import React from "react";
import { X, Plus } from "lucide-react";

export default function CustomerModal({
  isOpen,
  onClose,
  isEditMode,
  formData,
  setFormData,
  onSubmit,
  error,
  categories,
  paymentMethods,
  paySchedules,
  loading
}) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: "90%", maxWidth: "850px", maxHeight: "85vh", overflowY: "auto" }}>
        <div className="modal-header">
          <h3>{isEditMode ? "Modify Customer Details" : "Register New Customer"}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          {error && (
            <div className="login-error-alert animate-fade-in" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}
          <div className="customer-modal-grid">
            {/* Left Column: Basic Details */}
            <div>
              <h4 className="modal-section-title">General Information</h4>
              {/* Company Name */}
              <div className="form-group">
                <label className="form-label" htmlFor="name">Customer Company Name *</label>
                <input
                  id="name"
                  type="text"
                  required
                  className="form-input"
                  placeholder="e.g. Nexus Enterprises Inc."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              {/* Category & Status */}
              <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1fr 1fr" }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="category">Category *</label>
                  <select
                    id="category"
                    className="form-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="status">Customer Status *</label>
                  <select
                    id="status"
                    className="form-input"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Preferred Payment Method */}
              <div className="form-group">
                <label className="form-label" htmlFor="preferredPaymentMethod">Preferred Payment Method</label>
                <select
                  id="preferredPaymentMethod"
                  className="form-input"
                  value={formData.preferredPaymentMethod}
                  onChange={(e) => setFormData({ ...formData, preferredPaymentMethod: e.target.value })}
                >
                  <option value="">None Selected</option>
                  {paymentMethods.map(pm => (
                    <option key={pm.id} value={pm.name}>{pm.name}</option>
                  ))}
                </select>
              </div>

              {/* Pay Schedule */}
              <div className="form-group">
                <label className="form-label" htmlFor="paySchedule">Pay Schedule</label>
                <select
                  id="paySchedule"
                  className="form-input"
                  value={formData.paySchedule || ""}
                  onChange={(e) => setFormData({ ...formData, paySchedule: e.target.value })}
                >
                  <option value="">None Selected</option>
                  {paySchedules.map(ps => (
                    <option key={ps.id} value={ps.name}>{ps.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right Column: Company Address Section */}
            <div>
              <h4 className="modal-section-title">Address Details</h4>
              <div className="form-group">
                <label className="form-label" htmlFor="address">Street Address</label>
                <input
                  id="address"
                  type="text"
                  className="form-input"
                  placeholder="e.g. 100 Innovation Way"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="address2">Suite / Unit / Building</label>
                <input
                  id="address2"
                  type="text"
                  className="form-input"
                  placeholder="e.g. Suite 200"
                  value={formData.address2}
                  onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.7fr 0.8fr", gap: "0.75rem" }}>
                <div className="form-group">
                  <label className="form-label" htmlFor="city">City</label>
                  <input
                    id="city"
                    type="text"
                    className="form-input"
                    placeholder="e.g. New York"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="state">State</label>
                  <input
                    id="state"
                    type="text"
                    className="form-input"
                    placeholder="e.g. NY"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="zip">ZIP Code</label>
                  <input
                    id="zip"
                    type="text"
                    className="form-input"
                    placeholder="e.g. 10001"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  />
                </div>
              </div>

              {/* Billing Address Details */}
              <div style={{ marginTop: "1.5rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <h4 className="modal-section-title" style={{ margin: 0 }}>Bill To Address</h4>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setFormData({
                      ...formData,
                      billAddress: formData.address || "",
                      billAddress2: formData.address2 || "",
                      billCity: formData.city || "",
                      billState: formData.state || "",
                      billZip: formData.zip || ""
                    })}
                    style={{ padding: "0.2rem 0.5rem", minHeight: "auto", fontSize: "0.75rem", textTransform: "none" }}
                  >
                    Copy Shipping Address
                  </button>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="billAddress">Street Address</label>
                  <input
                    id="billAddress"
                    type="text"
                    className="form-input"
                    placeholder="e.g. 100 Innovation Way"
                    value={formData.billAddress || ""}
                    onChange={(e) => setFormData({ ...formData, billAddress: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="billAddress2">Suite / Unit / Building</label>
                  <input
                    id="billAddress2"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Suite 200"
                    value={formData.billAddress2 || ""}
                    onChange={(e) => setFormData({ ...formData, billAddress2: e.target.value })}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.7fr 0.8fr", gap: "0.75rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="billCity">City</label>
                    <input
                      id="billCity"
                      type="text"
                      className="form-input"
                      placeholder="e.g. New York"
                      value={formData.billCity || ""}
                      onChange={(e) => setFormData({ ...formData, billCity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="billState">State</label>
                    <input
                      id="billState"
                      type="text"
                      className="form-input"
                      placeholder="e.g. NY"
                      value={formData.billState || ""}
                      onChange={(e) => setFormData({ ...formData, billState: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="billZip">ZIP Code</label>
                    <input
                      id="billZip"
                      type="text"
                      className="form-input"
                      placeholder="e.g. 10001"
                      value={formData.billZip || ""}
                      onChange={(e) => setFormData({ ...formData, billZip: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Ship To Address Details */}
              <div style={{ marginTop: "1.5rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <h4 className="modal-section-title" style={{ margin: 0 }}>Ship To Address</h4>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setFormData({
                      ...formData,
                      shipAddress: formData.address || "",
                      shipAddress2: formData.address2 || "",
                      shipCity: formData.city || "",
                      shipState: formData.state || "",
                      shipZip: formData.zip || ""
                    })}
                    style={{ padding: "0.2rem 0.5rem", minHeight: "auto", fontSize: "0.75rem", textTransform: "none" }}
                  >
                    Copy Company Address
                  </button>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="shipAddress">Street Address</label>
                  <input
                    id="shipAddress"
                    type="text"
                    className="form-input"
                    placeholder="e.g. 7000 Loisdale Rd"
                    value={formData.shipAddress || ""}
                    onChange={(e) => setFormData({ ...formData, shipAddress: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="shipAddress2">Dock / Building / Suite</label>
                  <input
                    id="shipAddress2"
                    type="text"
                    className="form-input"
                    placeholder="e.g. Warehouse BldgG, Dock 2"
                    value={formData.shipAddress2 || ""}
                    onChange={(e) => setFormData({ ...formData, shipAddress2: e.target.value })}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.7fr 0.8fr", gap: "0.75rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="shipCity">City</label>
                    <input
                      id="shipCity"
                      type="text"
                      className="form-input"
                      placeholder="e.g. Newington"
                      value={formData.shipCity || ""}
                      onChange={(e) => setFormData({ ...formData, shipCity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="shipState">State</label>
                    <input
                      id="shipState"
                      type="text"
                      className="form-input"
                      placeholder="e.g. VA"
                      value={formData.shipState || ""}
                      onChange={(e) => setFormData({ ...formData, shipState: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="shipZip">ZIP Code</label>
                    <input
                      id="shipZip"
                      type="text"
                      className="form-input"
                      placeholder="e.g. 22150"
                      value={formData.shipZip || ""}
                      onChange={(e) => setFormData({ ...formData, shipZip: e.target.value })}
                    />
                  </div>
                </div>

                {/* Mark For (M/F) */}
                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label className="form-label" htmlFor="custMarkFor">Mark For (M/F)</label>
                  <input
                    id="custMarkFor"
                    type="text"
                    className="form-input"
                    placeholder="e.g. M/F: 2025-795632 Bozer, VA 22150"
                    value={formData.markFor || ""}
                    onChange={(e) => setFormData({ ...formData, markFor: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Points of Contact List */}
          <div style={{ marginTop: "1.25rem", borderTop: "1px solid var(--border-color)", paddingTop: "1rem", marginBottom: "1rem" }}>
            <h4 style={{ margin: "0 0 0.75rem 0", fontSize: "0.95rem", color: "var(--primary)" }}>Points of Contact</h4>
            {formData.pocs && formData.pocs.map((poc, index) => (
              <div key={index} style={{ border: "1px solid var(--border-color)", padding: "1rem", borderRadius: "6px", marginBottom: "1rem", backgroundColor: "rgba(255, 255, 255, 0.01)", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "var(--primary)" }}>Contact #{index + 1}</span>
                  {formData.pocs.length > 1 && (
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => {
                        const updatedPocs = formData.pocs.filter((_, i) => i !== index);
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ padding: "0.15rem 0.5rem", minHeight: "auto", fontSize: "0.75rem", color: "var(--danger)", borderColor: "var(--danger)" }}
                    >
                      Remove POC
                    </button>
                  )}
                </div>
                
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "0.75rem" }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`poc-name-${index}`}>Contact Name</label>
                    <input
                      id={`poc-name-${index}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. John Doe"
                      value={poc.name}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], name: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`poc-title-${index}`}>POC Title</label>
                    <input
                      id={`poc-title-${index}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. Purchasing Manager"
                      value={poc.title}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], title: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "0.75rem", marginTop: "0.5rem" }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor={`poc-email-${index}`}>Email Address</label>
                    <input
                      id={`poc-email-${index}`}
                      type="email"
                      className="form-input"
                      placeholder="e.g. john@customer.com"
                      value={poc.email}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], email: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" htmlFor={`poc-phone-${index}`}>Phone Number</label>
                    <input
                      id={`poc-phone-${index}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. 555-0100"
                      value={poc.phone}
                      onChange={(e) => {
                        const updatedPocs = [...formData.pocs];
                        updatedPocs[index] = { ...updatedPocs[index], phone: e.target.value };
                        setFormData({ ...formData, pocs: updatedPocs });
                      }}
                      style={{ margin: 0 }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setFormData({ ...formData, pocs: [...formData.pocs, { name: "", title: "", email: "", phone: "" }] })}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", justifyContent: "center", borderStyle: "dashed" }}
            >
              <Plus size={16} />
              Add Point of Contact
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {isEditMode ? "Save Changes" : "Register Customer"}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .customer-modal-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        .modal-section-title {
          margin: 0 0 1rem 0;
          font-size: 0.9rem;
          color: var(--primary);
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 700;
        }

        @media (min-width: 769px) {
          .customer-modal-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
}
